import { Component, Vue } from 'vue-property-decorator';
import { mapState, mapGetters, mapActions } from 'vuex';

import { ClientInfo, MultiLanguage, Process, ProcessConfigAuthorization, ProcessConfigFactory, ProcessConfigPayExemption, TurnWithReferences, Vacancies, VacanciesAttached } from '@/Domain/Entities';
import { OppositionType, InscriptionTabs, ProcessType, ProcessPhases, ProcessStatus, ProcessConfigurationTypes } from '@/Domain/enum';

import { Breadcrumb, Modal, Documents, Toggle, Authorizations, Payment, GeneralInfo, Requirements, Exams, Merits, SdModal, TurnPreference } from '@/Components';

import { ToasterService } from '@/Services/ToasterService';
import ProcessesService from '@/Services/ProcessesService';
import ValidationService from '@/Application/Services/ValidationService';

import * as notification from '../../../node_modules/saviafront/lib/js/compiled/notification';
import i18n from '../../lang';
import MasterDataService from '@/Services/MasterDataService';
import { IProcessReference } from '@/Domain/interfaces/Process/IProcessReference';
import AnalitycsService from '@/Services/AnalitycsService';
import { convertStringToBigInt } from '@/utils';

@Component({
    components: {
        Breadcrumb,
        Modal,
        Documents,
        Authorizations,
        GeneralInfo,
        Requirements,
        Exams,
        Merits,
        Toggle,
        Payment,
        SdModal,
        TurnPreference
    },
    computed: {
        ...mapState('featureFlagsStore', ['multiLanguage', 'preferenceForThePlace']),
        ...mapState('languagesStore', { currentLanguage: 'current' }),
        ...mapGetters('languagesStore', { availableLanguages: 'getTranslatableLanguages' }),

        breadcrumbInfo() {
            const breadcrumb: any[] = [];

            breadcrumb.push({ title: 'lang.router.editListProcess', link: 'ProcessList' });

            if (this.$route.params.id) {
                breadcrumb.push({ title: this.$data.process.title[this.$store.state.languagesStore.current] });
            } else {
                breadcrumb.push({ title: i18n.t('lang.router.newProcess', { typeProcess: i18n.t(ProcessType.translations[this.$route.params.type]) }) });
            }

            if (this.$data.process.editingMeritGroup) {
                breadcrumb.push({ title: 'lang.shared.groupsMerits' });
            }
            return breadcrumb;
        },
        anyPayExemptionFeePaymentNotHasAmount() {
            return this.$data.process.fees.payExemptionTypesView.find(payExemption => !payExemption.amount && payExemption.amount === null && payExemption.value);
        },
        isEditAnyComponent() {
            return this.$data.process.isCreatingOrEditingProcess();
        },
        hasExamsRequirementTest() {
            return this.$data.process.exams.length && this.$data.process.exams.some(exam => exam.isRequirementTest);
        },
        hasExamsWithoutRequirementTest() {
            return this.$data.process.exams.length && this.$data.process.exams.some(exam => !exam.isRequirementTest);
        },
        isMeritsTabDisabled() {
            return Number(this.$data.process.oppositionType) === OppositionType.enum.NOMINATION || Number(this.$data.process.oppositionType) === OppositionType.enum.FREE_DESIGNATION;
        },
        isExamsTabDisabled() {
            return (Number(this.$data.process.oppositionType) === OppositionType.enum.TENDER && !this.$data.process.hasRequirementWithTest) || Number(this.$data.process.oppositionType) === OppositionType.enum.FREE_DESIGNATION;
        }
    },
    methods: {
        ...mapActions('userStore', ['fetchAdminBlockedProcessesIds'])
    }
})

export default class ProcessEdit extends Vue {
    OppositionType = OppositionType;
    ProcessType = ProcessType;
    ProcessPhases = ProcessPhases;
    InscriptionTabs = InscriptionTabs;
    ProcessStatus = ProcessStatus;

    currentLanguage!: string;
    availableLanguages!: any;
    preferenceForThePlace!: boolean;
    anyPayExemptionFeePaymentNotHasAmount!: boolean;
    process: Process = null as any;
    type = null as any;
    processStatusSaved: number = 0;
    meritGroupSubGroupEmpty: any = [];
    tabSelected = InscriptionTabs.GENERAL;

    waitingResponse: boolean = false;
    invalidMerits: boolean = false;
    invalidCallTurns: boolean = false;
    examScoreError: boolean = false;
    invalidForm: boolean = false;
    invalidVacancies: boolean = false;
    meritAlreadyIncluded: boolean = false;
    meritScoreError: boolean = false;
    meritMaxScoreParentError: boolean = false;
    isInvalidScoreChildren: boolean = false;
    meritSubGroupChildrenError: boolean = false;
    subGroupMaxScoreParentError: boolean = false;
    submitted: boolean = false;
    turnsAreCheched: boolean = true;
    hasMeritGroupSubGroupEmpties: boolean = false;
    clientInfo: ClientInfo = new ClientInfo({});
    urlApplicant: string = '';
    showModalPrice: boolean = false;
    acceptDeletePayments: boolean = false;
    processConfigurationPayExemptions: ProcessConfigPayExemption[] = [];
    processConfigurationAuthorizations: ProcessConfigAuthorization[] = [];
    fetchAdminBlockedProcessesIds!: (dni: string) => void;
    userConnectedDni!: string;
    showModalPostInscriptionMeritPresentation: boolean = false;
    turnStructure: TurnWithReferences = new TurnWithReferences();

    $refs!: {
        processForm: HTMLFormElement,
        exams: Exams
    };

    updateProcess(value) {
        this.process.hasRequirementWithTest = value;
        this.$refs.exams.getExam();
    }

    updateReference(updateReferenceData: { reference: IProcessReference, index: number }) {
        this.$set(this.turnStructure.turnActive!.references, updateReferenceData.index, updateReferenceData.reference);
        this.updateTurnCount();
    }

    updateTurnCount() {
        this.turnStructure.updateCount();
    }

    updateExtraVacancies(extraVacancies: { value: number, turnType: number}) {
        this.process.vacanciesAttached = this.process.vacanciesAttached ? new VacanciesAttached(this.process.vacanciesAttached.toServer()) : new VacanciesAttached({});
        this.process.vacanciesAttached.setVacancies(extraVacancies.value, extraVacancies.turnType);
        this.turnStructure.addExtraVacancies(this.process.vacanciesAttached);
        this.updateTurnCount();
    }

    addReference() {
        this.turnStructure.turnActive!.references.push({ description: new MultiLanguage({ [this.availableLanguages[0]]: '', [this.availableLanguages[1]]: '' }), turn: this.turnStructure.turnActive!.type, reference: '', totalVacancies: 0, totalExtraVacancies: 0, remainingVacancies: 0 });
    }

    removeReference(indexToRemove: number) {
        this.turnStructure.turnActive!.references.splice(indexToRemove, 1);
        this.updateTurnCount();
    }

    changeActiveTurn(type: number) {
        this.turnStructure.changeActiveTurn(type);
    }

    changeIsExtraVacanciesActive() {
        this.turnStructure.changeIsExtraVacanciesActive();
    }

    confirmWithModalPublic() {
        this.process.stateToggle ? this.process.isCheckNeeded = true : this.submit();
    }

    closeModalPayment() {
        this.process.fees.hasFeePayment = true;
        this.showModalPrice = false;
    }

    acceptModalPayment() {
        this.process.fees.resetReasons();
        this.showModalPrice = false;
        this.acceptDeletePayments = true;
        this.submit();
    }

    postInscriptionMeritPresentationValidation() {
        if (this.$data.process.phase > ProcessPhases.enum.EVALUATION && this.$data.process.allowPostInscriptionMeritPresentation) {
            this.showModalPostInscriptionMeritPresentation = true;
            this.$data.process.allowPostInscriptionMeritPresentation = false;
        }
    }

    closeModalPostInscriptionMeritPresentation() {
        this.showModalPostInscriptionMeritPresentation = false;
    }

    goToApplicantPreview() {
        this.urlApplicant = this.clientInfo.getHost();
        window.open(this.urlApplicant + '/inscription.html?id=' + this.process.id + '&type=' + this.$route.params.type + '&preview=true');
    }

    getProcessAction() {
        if (this.process.id) {
            return ProcessesService.updateProcess(this.process.toServer(this.process.stateToggle));
        }
        return ProcessesService.addProcess(this.process.toServer(this.process.stateToggle));
    }

    selectTab(selectedTab: InscriptionTabs) {
        this.type = Number(this.process.oppositionType);

        if (this.type === OppositionType.enum.FREE_DESIGNATION && (selectedTab === InscriptionTabs.EXAMS || selectedTab === InscriptionTabs.MERITS)) {
            return;
        }
        if (this.type === OppositionType.enum.NOMINATION && selectedTab === InscriptionTabs.MERITS) {
            return;
        }
        if ((this.type === OppositionType.enum.TENDER && !this.process.hasRequirementWithTest) && selectedTab === InscriptionTabs.EXAMS) {
            return;
        }
        if (this.process.type === ProcessType.enum.POSTPROVISION && selectedTab === InscriptionTabs.AUTHORIZATIONS) {
            return;
        }

        this.process.setEditAndCreateToFalse();

        this.tabSelected = selectedTab;
    }

    cancel() {
        this.process.stateToggle = false;
        this.process.status = Number(this.process.stateToggle);
        if (!this.process.fees.hasFeePayment && this.process.fees.amount) {
            this.process.fees.hasFeePayment = true;
        }
    }

    setNoError() {
        this.meritMaxScoreParentError = false;
        this.isInvalidScoreChildren = false;
        this.meritSubGroupChildrenError = false;
        this.subGroupMaxScoreParentError = false;
        this.meritScoreError = false;
        this.meritAlreadyIncluded = false;
    }

    submit() {
        if (!this.process.fees.hasFeePayment && this.process.canProcessBePaidOnline && this.process.fees.amount && !this.acceptDeletePayments && !this.process.isCheckNeeded) {
            this.showModalPrice = true;
            return;
        }
        if (!this.process.fees.hasFeePayment) {
            this.process.fees.resetReasons();
        }
        this.submitted = true;
        this.process.isCheckNeeded = false;
        if (this.process.oppositionType) {
            this.process.oppositionType = Number(this.process.oppositionType);
        }
        this.invalidForm = !ValidationService.validateFormRequired(this.$refs.processForm);
        this.invalidVacancies = this.preferenceForThePlace && this.process.type === ProcessType.enum.CALL ? false : !this.process.areVacanciesValid();
        this.invalidMerits = !this.process.areMeritsValid();
        this.meritGroupSubGroupEmpty = this.process.areMeritsChildsOrMeritSubGroupsEmpties();
        this.hasMeritGroupSubGroupEmpties = this.meritGroupSubGroupEmpty.length && this.process.stateToggle;
        
        this.invalidCallTurns = this.preferenceForThePlace && this.process.type === ProcessType.enum.CALL ? !this.turnStructure.areTurnsValid(this.availableLanguages) : false; 
        if (this.invalidCallTurns) {
            this.changeActiveTurn(this.turnStructure.getTurnsWithErrors(this.availableLanguages)[0]!.type);
        }

        if (this.$data.process.type === ProcessType.enum.BAG || this.$data.process.type === ProcessType.enum.POSTPROVISION) {
            this.turnsAreCheched = this.process.turns.isValidTurnsChecked();
        }
        if (this.waitingResponse || this.invalidForm || this.invalidMerits || !this.turnsAreCheched || this.hasMeritGroupSubGroupEmpties || this.invalidVacancies || this.invalidCallTurns) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.process.stateToggle = this.processStatusSaved > 0;
            this.process.status = this.processStatusSaved;
            return;
        }

        this.manageReferencesWhenProcessIsCall();

        const isReferencesValid = this.hasValidReferences(this.process.references);
        const isVacanciesValid = this.hasValidVacancies(this.process.vacancies);

        if (this.process.id && this.process.type === ProcessType.enum.CALL && this.preferenceForThePlace && !isReferencesValid && !isVacanciesValid) {
            this.invalidCallTurns = true;
            this.invalidForm = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.process.stateToggle = this.processStatusSaved > 0;
            this.process.status = this.processStatusSaved;
            return;
        }

        this.type = this.process.oppositionType;
        this.waitingResponse = true;
        this.process.status = Number(this.process.stateToggle);
        const action = this.getProcessAction();
        action.then(response => {
            if (this.$route.name === 'CallNew' || this.$route.name === 'BagNew' || this.$route.name === 'PostProvisionNew') {
                this.process = new Process(response, this.processConfigurationPayExemptions, this.$store.state.processConfigStore.processConfigGroups);
                this.process.createAutorizations(this.processConfigurationAuthorizations);
                this.$router.push({ name: ProcessType.routeEdit[this.$route.params.type], params: { id: response.id, type: response.type }});
            }
            if (this.process.oppositionType === OppositionType.enum.NOMINATION) {
                this.process.meritGroups = [];
                this.process.allowPostInscriptionMeritPresentation = false;
                this.process.meritPresentationStartDate = null;
                this.process.meritPresentationEndDate = null;
            }
            if (this.process.oppositionType === OppositionType.enum.TENDER && this.process.hasRequirementWithTest) {
                this.process.exams = this.process.exams.filter(exam => exam.isRequirementTest);
            }
            if (this.process.oppositionType === OppositionType.enum.TENDER && !this.process.hasRequirementWithTest) {
                this.process.exams = [];
            }
            if (!this.process.requirements.length) {
                this.process.exams = this.process.exams.filter(exam => !exam.isRequirementTest);
            }
            this.process.references = response.references ? [...response.references] : [];
            this.buildTurnsWhenProcessIsCall();
            this.sendAnalytics();
            this.meritAlreadyIncluded = false;
            this.process.stateToggle = this.process.status > 0;
            this.processStatusSaved = this.process.status;
            ToasterService.showSuccess(i18n.t('lang.toaster.saveChanged') as any, i18n.t('lang.toaster.goodJob') as any);
            this.waitingResponse = false;
            this.acceptDeletePayments = false;
            this.updateUserStore();
        }).catch(() => {
            this.waitingResponse = false;
            this.process.stateToggle = this.processStatusSaved > 0;
            this.process.status = this.processStatusSaved;
        }).finally(() => {
            this.submitted = false;
        });
    }

    manageReferencesWhenProcessIsCall() {
        if (this.process.type !== ProcessType.enum.CALL || !this.preferenceForThePlace) {
            return;
        }
    
        if (!this.process.id) {
            this.process.vacancies = new Vacancies({
                free: 0,
                internal: 0,
                internalDisability: 0,
                freeDisability: 0,
                mobility: 0,
                total: 0
            });
        } else if (this.turnStructure.anyTurnHasReferences()) {
            this.updateReferencesFromTurnStructure();
        } else {
            this.updateVacanciesFromTurnStructure();
        }
    }
    
    updateReferencesFromTurnStructure() {
        this.turnStructure.bopNumber = this.process.bopNumber;
        this.process.references = this.turnStructure.getReferences();
        this.process.vacancies = new Vacancies({
            free: 0,
            internal: 0,
            internalDisability: 0,
            freeDisability: 0,
            mobility: 0,
            total: 0
        });
    }
    hasValidVacancies(data: Vacancies): boolean {
        return Object.values(data).some(value => value > 0);
    }

    updateVacanciesFromTurnStructure() {
        this.process.vacancies = this.turnStructure.getVacancies();
        this.process.references = [];
    }

    hasValidReferences(references: IProcessReference[]): boolean {
        return (references && references.length > 0);
    }

    buildTurnsWhenProcessIsCall() {
        if (this.process.type !== ProcessType.enum.CALL || !this.preferenceForThePlace) {
            return;
        }
        if (this.process.references.length > 0) {
            this.turnStructure.bopNumber = this.process.bopNumber;
            this.turnStructure.buildTurns(this.process.references);
        } else {
           this.turnStructure.buildTurns(this.process.vacancies);
        }
        if (this.process.vacanciesAttached) {
            this.turnStructure.addExtraVacancies(this.process.vacanciesAttached);
        }

    }

    sendAnalytics() {
        if (this.process.type === ProcessType.enum.CALL && this.preferenceForThePlace && this.process.references.length) {
            AnalitycsService.sendEvent({ 
                name: 'process_with_references', 
                data: { processName: this.process.title[this.availableLanguages[0]] } 
            });
        }
    }

    setMeritScoreError() {
        this.meritScoreError = true;
    }

    setMeritMaxScoreParentError() {
        this.meritMaxScoreParentError = true;
    }

    setSubGroupMaxScoreParentError() {
        this.subGroupMaxScoreParentError = true;
    }

    setIsInvalidScoreChildren() {
        this.isInvalidScoreChildren = true;
    }

    setMeritSubGroupChildrenError() {
        this.meritSubGroupChildrenError = true;
    }

    setMeritAlreadyIncluded() {
        this.meritAlreadyIncluded = true;
    }

    updateUserStore() {
        this.fetchAdminBlockedProcessesIds(this.userConnectedDni);
    }

    getProcessById() {
        ProcessesService.getProcessById(this.$route.params.id, Number(this.$route.params.type))
            .then((response: Process) => {
                this.process = new Process(response, this.processConfigurationPayExemptions, this.$store.state.processConfigStore.processConfigGroups);
                ProcessesService.getIsInUse(this.process.id)
                    .then((resp: boolean) => {
                        this.process.isUsed = resp;
                    }).catch(error => console.log(error));
                this.process.stateToggle = this.process.status > 0;
                this.processStatusSaved = this.process.status;
                this.process.createAutorizations(this.processConfigurationAuthorizations);
                this.type = this.process.oppositionType;
                this.buildTurnsWhenProcessIsCall();
                this.$nextTick(() => {
                    notification.initialize();
                });
            })
            .catch(error => console.log(error));
    }

    initClientInfo() {
        MasterDataService.getClientInfo()
            .then(responseMasterData => {
                this.clientInfo = new ClientInfo(responseMasterData);
            })
            .catch(error => console.log(error));
    }

    getProcessConfigurationPayExemptionsList() {
        return MasterDataService.getProcessesConfiguration(ProcessConfigurationTypes.enum.PAYEXEMPTIONS)
            .then((response: any) => {
                this.processConfigurationPayExemptions = response.map(
                    processConfig => { 
                        return ProcessConfigFactory.create(ProcessConfigurationTypes.enum.PAYEXEMPTIONS, processConfig);
                    }
                // CON-451: al ofertar descuentos en un proceso se muestran solo los ACTIVOS. Se incluyen los
                // personalizados (type nulo, se identifican por Guid) y se excluyen None/NoReason (type <= 1).
                ).filter(processConfig => processConfig.active &&
                        (processConfig.type === null || processConfig.type === undefined ||
                         convertStringToBigInt(processConfig.type) > convertStringToBigInt(processConfig.filterMinimumValidType)));
            }).catch(error => console.log(error));
    }

    getProcessConfigurationAuthorizationsList() {
        return MasterDataService.getProcessesConfiguration(ProcessConfigurationTypes.enum.AUTHORIZATIONS)
            .then((response: any[]) => {
                this.processConfigurationAuthorizations = response.map(
                    processConfig => ProcessConfigFactory.create(
                        ProcessConfigurationTypes.enum.AUTHORIZATIONS, processConfig)
                    );
                // CON-441: al ofertar autorizaciones en un proceso se muestran solo las ACTIVAS.
                this.processConfigurationAuthorizations = this.processConfigurationAuthorizations.filter(
                    processConfig => processConfig.active &&
                    processConfig.type &&
                    convertStringToBigInt(processConfig.type) >= convertStringToBigInt(processConfig.filterMinimumValidType));
            }).catch(error => console.log(error));
    }

    updatedTurnReferences() {
        this.getProcessById();
    }

    created() {
        this.initClientInfo();
        // CON-1030: los catálogos maestros (descuentos/autorizaciones) deben quedar poblados
        // ANTES de construir el Process (getProcessById / alta nueva), que los consume de forma
        // síncrona en su constructor. Antes se disparaban en paralelo sin sincronización y, si la
        // respuesta del proceso llegaba primero, el Process se construía con el catálogo vacío.
        Promise.all([
            this.getProcessConfigurationPayExemptionsList(),
            this.getProcessConfigurationAuthorizationsList()
        ]).then(() => {
            if (this.$route.params.id) {
                this.getProcessById();
            } else {
                this.process = new Process({ type: this.$route.params.type, showProcessTypeToCandidates: true, showVacanciesToCandidates: true });
                this.$nextTick(() => {
                    notification.initialize();
                });
            }
        });
    }
}
